<template>
  <section
    v-if="story"
    :class="['tci', 'tci--' + story.tone]"
    role="status"
    aria-live="polite"
    aria-label="Работа с ТМС: что случилось и что делать"
  >
    <header class="tci__head">
      <span class="tci__icon" aria-hidden="true">{{ icon }}</span>
      <div class="tci__titles">
        <h5 class="tci__title">Рейс не принят в TMS</h5>
        <span :class="['tci__pill', 'tci__pill--' + story.tone]">{{ story.status }}</span>
      </div>
      <button class="tci__help" type="button" title="Как это работает" @click="helpOpen = !helpOpen">?</button>
    </header>

    <ol class="tci__story">
      <li><span class="tci__step">Что случилось</span><p>{{ story.what }}</p></li>
      <li><span class="tci__step">На что влияет</span><p>{{ story.impact }}</p></li>
      <li><span class="tci__step">Что делать</span><p>{{ story.next }}</p></li>
    </ol>

    <transition name="tci-fade">
      <p v-if="helpOpen" class="tci__hint">
        После регистрации водителя на погрузке TMS не даёт менять машину и прицеп. Поменять их может только
        заказчик: система собирает письмо-просьбу открыть рейс с правильными данными из ERP. Подробнее —
        <router-link :to="{ path: '/knowledge', query: { a: 'whats-new' } }">«Что нового»</router-link>.
      </p>
    </transition>

    <div v-if="story.canRequest" class="tci__actions">
      <button class="tci__btn tci__btn--gold" type="button" @click="openModal">
        Попросить заказчика открыть рейс
      </button>
    </div>
    <p v-if="resultText" class="tci__result">{{ resultText }}</p>

    <details v-if="lastError" class="tci__tech">
      <summary>Подробности для IT</summary>
      <TmsLastErrorCard :error="lastError" :log-link="logLink" />
    </details>

    <transition name="tci-fade">
      <div v-if="modal" class="tci-modal" role="dialog" aria-modal="true" aria-label="Просьба заказчику открыть рейс"
           @click.self="closeModal" @keydown.esc="closeModal">
        <div class="tci-modal__card">
          <h4 class="tci-modal__title">Попросить заказчика открыть рейс {{ trip.trip_id }}</h4>
          <p class="tci-modal__lead">
            Отметьте, что нужно исправить. Правильные данные система возьмёт из ERP — проверьте их в карточке
            исполнителя до отправки.
          </p>

          <fieldset class="tci-modal__parts">
            <legend>Исправить</legend>
            <label v-for="part in PARTS" :key="part" :class="['tci-chip', { 'tci-chip--on': parts.includes(part) }]">
              <input type="checkbox" :value="part" v-model="parts" @change="loadPreview" />
              {{ PART_LABELS[part] }}
            </label>
          </fieldset>

          <label class="tci-modal__reason">
            <span>Причина (необязательно)</span>
            <textarea v-model="reason" maxlength="500" rows="2"
                      placeholder="Например: в TMS стоит Н844ЕО48 вместо У516РМ96" @change="loadPreview" />
          </label>

          <div class="tci-modal__letter" aria-live="polite">
            <div v-if="previewLoading" class="tci-skeleton" aria-label="Собираю письмо">
              <span></span><span></span><span></span>
            </div>
            <p v-else-if="previewError" class="tci-modal__error">{{ previewError }}</p>
            <template v-else-if="preview">
              <p v-if="preview.problem" class="tci-modal__error">{{ preview.problem }}</p>
              <template v-else>
                <p class="tci-modal__meta"><b>Кому:</b> {{ preview.recipients }}</p>
                <p class="tci-modal__meta"><b>Тема:</b> {{ preview.subject }}</p>
                <pre class="tci-modal__body">{{ preview.body }}</pre>
              </template>
            </template>
          </div>

          <p class="tci-modal__note">
            Письмо встанет в «Письма на подтверждение». Пока у писем заказчику включён тестовый режим, его
            отправляет руководитель кнопкой «Отправить».
          </p>
          <p v-if="sendError" class="tci-modal__error">{{ sendError }}</p>

          <div class="tci-modal__actions">
            <button class="tci__btn" type="button" @click="closeModal">Отмена</button>
            <button class="tci__btn tci__btn--gold" type="button" :disabled="!canSend" @click="send">
              {{ sending ? 'Ставлю в очередь…' : 'Отправить на подтверждение' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'
import TmsLastErrorCard from '@/components/TmsLastErrorCard.vue'
import { fetchCorrectionPreview, requestCorrection } from '@/backend/tripCorrectionApi.js'
import {
  PARTS, PART_LABELS, guessParts, issueStory, partsQuery, requestResultText,
} from './tripCorrectionLogic.mjs'

const props = defineProps({
  trip: { type: Object, required: true },
  lastError: { type: Object, default: null },
  logLink: { type: String, default: '' },
})

const helpOpen = ref(false)
const modal = ref(false)
const parts = ref([])
const reason = ref('')
const preview = ref(null)
const previewLoading = ref(false)
const previewError = ref('')
const sending = ref(false)
const sendError = ref('')
const requested = ref(null)

const errorText = computed(() => props.trip?.error_message || props.lastError?.tms_message || props.lastError?.error_detail || '')
const story = computed(() => issueStory(errorText.value, { requested: requested.value }))
const icon = computed(() => ({ urgent: '!', warn: 'i', wait: '…' }[story.value?.tone] || 'i'))
const resultText = computed(() => requestResultText(requested.value))
const canSend = computed(() => !sending.value && parts.value.length > 0 && preview.value && !preview.value.problem
  && preview.value.enabled !== false)

function apiError(error, fallback) {
  return error?.response?.data?.detail || fallback
}

async function loadPreview() {
  previewLoading.value = true
  previewError.value = ''
  try {
    preview.value = await fetchCorrectionPreview(props.trip.id, partsQuery(parts.value), reason.value)
    if (preview.value?.enabled === false) previewError.value = 'Просьбы заказчику сейчас выключены в Пульте правил.'
  } catch (error) {
    preview.value = null
    previewError.value = apiError(error, 'Не удалось собрать письмо. Обновите страницу и попробуйте ещё раз.')
  } finally {
    previewLoading.value = false
  }
}

function openModal() {
  parts.value = guessParts(errorText.value)
  reason.value = ''
  sendError.value = ''
  modal.value = true
  loadPreview()
}

function closeModal() {
  if (!sending.value) modal.value = false
}

async function send() {
  if (!canSend.value) return
  sending.value = true
  sendError.value = ''
  try {
    requested.value = await requestCorrection(props.trip.id, parts.value, reason.value)
    modal.value = false
  } catch (error) {
    sendError.value = apiError(error, 'Письмо не поставлено в очередь. Попробуйте ещё раз.')
  } finally {
    sending.value = false
  }
}
</script>

<style scoped>
.tci {
  --ink: #1f2430; --muted: #6b7280; --line: #e7e5df; --paper: #fbfaf7; --gold: #f2b705; --gold-ink: #8a6400;
  --red: #c2410c; --red-bg: #fff4ed; --amber-bg: #fff8e6; --blue-bg: #f2f5fb;
  margin-top: 14px; padding: 16px 18px; border-radius: 18px; background: var(--paper); color: var(--ink);
  border: 1px solid var(--line); border-left: 5px solid var(--gold); text-align: left;
  box-shadow: 0 6px 24px rgba(31, 36, 48, .06); animation: tci-in .28s ease-out both;
}
.tci--urgent { border-left-color: var(--red); }
.tci--wait { border-left-color: #6b8acb; }
.tci__head { display: flex; align-items: center; gap: 12px; }
.tci__icon {
  display: inline-grid; place-items: center; width: 30px; height: 30px; border-radius: 999px; flex: none;
  font-weight: 800; background: var(--amber-bg); color: var(--gold-ink);
}
.tci--urgent .tci__icon { background: var(--red-bg); color: var(--red); animation: tci-pulse 2s ease-in-out infinite; }
.tci--wait .tci__icon { background: var(--blue-bg); color: #3b5aa0; }
.tci__titles { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; flex: 1; }
.tci__title { margin: 0; font-size: 16px; font-weight: 800; letter-spacing: .1px; }
.tci__pill { font-size: 12px; font-weight: 700; padding: 3px 10px; border-radius: 999px; background: var(--amber-bg); color: var(--gold-ink); }
.tci__pill--urgent { background: var(--red-bg); color: var(--red); }
.tci__pill--wait { background: var(--blue-bg); color: #3b5aa0; }
.tci__help {
  width: 26px; height: 26px; border-radius: 999px; border: 1px solid var(--line); background: #fff;
  color: var(--muted); font-weight: 700; cursor: pointer;
}
.tci__story { list-style: none; margin: 12px 0 0; padding: 0; display: grid; gap: 8px; }
.tci__story li { display: grid; grid-template-columns: 128px 1fr; gap: 10px; align-items: baseline; }
.tci__step { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px; color: var(--muted); }
.tci__story p { margin: 0; line-height: 1.45; font-size: 14px; }
.tci__hint { margin: 10px 0 0; padding: 10px 12px; border-radius: 12px; background: #fff; border: 1px dashed var(--line);
  font-size: 13px; color: var(--muted); line-height: 1.45; }
.tci__actions { margin-top: 12px; display: flex; gap: 10px; flex-wrap: wrap; }
.tci__btn {
  border: 1px solid var(--line); background: #fff; color: var(--ink); border-radius: 999px; padding: 9px 18px;
  font-size: 14px; font-weight: 700; cursor: pointer; transition: transform .15s ease, box-shadow .15s ease;
}
.tci__btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(31, 36, 48, .1); }
.tci__btn:disabled { opacity: .5; cursor: not-allowed; }
.tci__btn--gold { background: var(--gold); border-color: var(--gold); color: #1f2430; }
.tci__btn:focus-visible, .tci__help:focus-visible { outline: 3px solid rgba(242, 183, 5, .45); outline-offset: 2px; }
.tci__result { margin: 10px 0 0; font-size: 13.5px; color: #3b5aa0; font-weight: 600; }
.tci__tech { margin-top: 10px; font-size: 13px; color: var(--muted); }
.tci__tech summary { cursor: pointer; }

.tci-modal { position: fixed; inset: 0; z-index: 1000; display: grid; place-items: center; padding: 16px;
  background: rgba(31, 36, 48, .38); }
.tci-modal__card { width: min(640px, 100%); max-height: calc(100vh - 32px); overflow: auto; background: var(--paper);
  border-radius: 20px; padding: 20px 22px; box-shadow: 0 24px 60px rgba(31, 36, 48, .25); animation: tci-in .22s ease-out both; }
.tci-modal__title { margin: 0; font-size: 18px; font-weight: 800; }
.tci-modal__lead, .tci-modal__note { margin: 6px 0 0; font-size: 13.5px; color: var(--muted); line-height: 1.45; }
.tci-modal__parts { border: 0; padding: 0; margin: 14px 0 0; display: flex; gap: 8px; flex-wrap: wrap; }
.tci-modal__parts legend { font-size: 12px; font-weight: 700; text-transform: uppercase; color: var(--muted); margin-bottom: 6px; }
.tci-chip { display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: 999px; cursor: pointer;
  border: 1px solid var(--line); background: #fff; font-weight: 600; font-size: 14px; }
.tci-chip--on { border-color: var(--gold); background: var(--amber-bg); }
.tci-chip input { accent-color: var(--gold); }
.tci-modal__reason { display: grid; gap: 6px; margin-top: 14px; font-size: 13px; color: var(--muted); }
.tci-modal__reason textarea { border: 1px solid var(--line); border-radius: 12px; padding: 8px 10px; font: inherit;
  color: var(--ink); background: #fff; resize: vertical; }
.tci-modal__letter { margin-top: 14px; padding: 12px 14px; border-radius: 14px; background: #fff; border: 1px solid var(--line); min-height: 72px; }
.tci-modal__meta { margin: 0 0 4px; font-size: 13.5px; }
.tci-modal__body { margin: 8px 0 0; white-space: pre-wrap; font-family: inherit; font-size: 13.5px; line-height: 1.5; color: var(--ink); }
.tci-modal__error { margin: 8px 0 0; color: var(--red); font-size: 13.5px; font-weight: 600; }
.tci-modal__actions { margin-top: 16px; display: flex; justify-content: flex-end; gap: 10px; flex-wrap: wrap; }
.tci-skeleton { display: grid; gap: 8px; }
.tci-skeleton span { height: 12px; border-radius: 6px; background: linear-gradient(90deg, #f1efe9, #faf9f6, #f1efe9);
  background-size: 200% 100%; animation: tci-shimmer 1.2s linear infinite; }
.tci-skeleton span:nth-child(2) { width: 80%; }
.tci-skeleton span:nth-child(3) { width: 60%; }

.tci-fade-enter-active, .tci-fade-leave-active { transition: opacity .18s ease; }
.tci-fade-enter-from, .tci-fade-leave-to { opacity: 0; }

@keyframes tci-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes tci-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(194, 65, 12, .28); } 50% { box-shadow: 0 0 0 6px rgba(194, 65, 12, 0); } }
@keyframes tci-shimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }

@media (max-width: 640px) {
  .tci__story li { grid-template-columns: 1fr; gap: 2px; }
  .tci-modal__actions { justify-content: stretch; }
  .tci-modal__actions .tci__btn { flex: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .tci, .tci-modal__card, .tci--urgent .tci__icon, .tci-skeleton span { animation: none; }
  .tci__btn { transition: none; }
}
</style>
